import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TransferService from '../services/TransferService';
import AuthService from '../services/AuthService';
import ConfirmationCancelTransferModal from './ConfirmationCancelTransferModal';
import GeneralSettingsService from '../services/GeneralSettingsService';

const ASSISTANCE_NUMBER = '+1 587-989-4933';

// ─── Hook export PDF détail ───────────────────────────────────────────────────
const useExportDetailPdf = () => {
    const [isExporting, setIsExporting] = useState(false);
    const [downloadReady, setDownloadReady] = useState(false);
    const [blobUrl, setBlobUrl] = useState(null);
    const [fileName, setFileName] = useState('');

    const extractFileName = (headers, fallback) => {
        try {
            const disposition = headers?.['content-disposition'];
            if (disposition) {
                const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match?.[1]) return match[1].replace(/['"]/g, '').trim();
            }
        } catch (_) {}
        return fallback;
    };

    const handleExport = async (transferId, referenceNumber) => {
        if (!transferId) return;
        setIsExporting(true);
        setDownloadReady(false);

        try {
            const response = await TransferService.exportTransferDetailPdf(transferId);

            const defaultFileName = `Transfert_${referenceNumber || transferId}.pdf`;
            const resolvedFileName = extractFileName(response.headers, defaultFileName);

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);

            setBlobUrl(url);
            setFileName(resolvedFileName);
            setDownloadReady(true);
        } catch (error) {
            console.error("Erreur lors de l'export PDF:", error);
            toast.error("Erreur lors de la génération du PDF. Veuillez réessayer.", {
                position: "top-right", autoClose: 5000,
            });
        } finally {
            setIsExporting(false);
        }
    };

    const handleDownload = () => {
        if (!blobUrl || !fileName) return;
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`✅ "${fileName}" téléchargé avec succès !`, {
            position: "top-right", autoClose: 4000,
        });
        reset();
    };

    const reset = () => {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
        setFileName('');
        setDownloadReady(false);
    };

    return { isExporting, downloadReady, fileName, handleExport, handleDownload, reset };
};

// ─── Composant ────────────────────────────────────────────────────────────────
const TransferDetailModal = ({ show, onHide, transfer, onRefresh }) => {
    const [isCancelling, setIsCancelling] = useState(false);
    const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [isPaymentDirectivesVisible, setIsPaymentDirectivesVisible] = useState(false);
    const [paymentSettings, setPaymentSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { isExporting, downloadReady, fileName, handleExport, handleDownload, reset } = useExportDetailPdf();

    // Réinitialiser l'état export quand on change de transfert ou ferme la modal
    useEffect(() => {
        reset();
    }, [transfer?.id, show]);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            const user = await AuthService.getCurrentUser();
            setCurrentUser(user);
        };
        fetchCurrentUser();
    }, []);

    useEffect(() => {
        const fetchPaymentSettings = async () => {
            try {
                setLoading(true);
                const data = await GeneralSettingsService.getAllParameters();
                const firstParameter = data[0];
                setPaymentSettings({
                    emailInterac: firstParameter.emailInterac,
                    interacName: firstParameter.interacName,
                    securityQuestion: firstParameter.securityQuestion,
                    securityAnswer: firstParameter.securityAnswer
                });
                setError(null);
            } catch (err) {
                console.error('Erreur lors de la récupération des paramètres:', err);
                setError('Impossible de charger les directives de paiement');
            } finally {
                setLoading(false);
            }
        };

        if (show && transfer?.transactionStatus === 'PENDING') {
            fetchPaymentSettings();
        }
    }, [show, transfer?.transactionStatus]);

    const togglePaymentDirectives = () => setIsPaymentDirectivesVisible(v => !v);

    const formatMoney = (amount, currency) => {
        if (amount === null || amount === undefined || amount === '') return 'N/A';
        const number = typeof amount === 'string'
            ? parseFloat(amount.replace(/\s/g, '').replace(',', '.'))
            : amount;
        if (isNaN(number)) return 'N/A';
        const hasDecimal = number % 1 !== 0;
        const formattedAmount = number.toLocaleString('fr-FR', {
            minimumFractionDigits: hasDecimal ? 2 : 0,
            maximumFractionDigits: hasDecimal ? 2 : 0
        });
        return currency ? `${formattedAmount} ${currency}` : formattedAmount;
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'PENDING':  return 'En attente';
            case 'SUCCESS':  return 'Transfert effectué';
            case 'REJECTED': return 'Transfert rejeté';
            default:         return status;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING':  return '#FF9500';
            case 'SUCCESS':  return '#34C759';
            case 'REJECTED': return '#FF3B30';
            default:         return '#000000';
        }
    };

    const getMethodColor = (method) => {
        switch (method) {
            case 'BANK':             return '#007AFF';
            case 'ORANGE_MONEY':     return '#FF9500';
            case 'MTN_MOBILE_MONEY': return '#FFCC00';
            case 'WAVE':             return '#0c76ed';
            default:                 return '#000000';
        }
    };

    const getCurrency = (country) => country === 'Cameroun' ? 'XAF' : 'XOF';

    const getReceiverAmountLabel = () => {
        if (!transfer?.transactionReceiverAmount || transfer?.transactionStatus === 'REJECTED') return null;
        switch (transfer?.transactionStatus) {
            case 'PENDING':  return 'Le destinataire recevra';
            case 'SUCCESS':  return 'Le destinataire a reçu';
            default:         return null;
        }
    };

    const getAccountDetails = () => {
        if (!transfer) return null;
        const { withdrawalMethod, recipientDetails } = transfer;
        const { recipientAccountInfo } = recipientDetails;
        if (withdrawalMethod === 'BANK') {
            const bankAccount = recipientAccountInfo.bankAccountInfos.find(
                acc => acc.id === transfer.recipientBankAccountInfo
            );
            return bankAccount ? { type: 'BANK', details: bankAccount } : null;
        } else if (['ORANGE_MONEY', 'MTN_MOBILE_MONEY', 'WAVE'].includes(withdrawalMethod)) {
            const walletAccount = recipientAccountInfo.walletAccountInfos.find(
                acc => acc.id === transfer.recipientWalletAccountInfo
            );
            return walletAccount ? { type: 'WALLET', details: walletAccount } : null;
        }
        return null;
    };

    const handleCancelClick = async () => {
        if (!transfer) return;
        const currentUser = AuthService.getCurrentUser();
        if (!currentUser) {
            toast.error('Utilisateur non connecté.', { position: "top-right", autoClose: 5000 });
            setIsCancelling(false);
            setIsConfirmationModalVisible(false);
            return;
        }
        setIsCancelling(true);
        const transferRequestDTO = {
            ...transfer,
            rejectReason: "Annuler par l'utilisateur",
            lastModifiedBy: currentUser.username
        };
        try {
            await TransferService.rejectTransfer(transferRequestDTO);
            toast.success('Le transfert a été annulé avec succès.', { position: "top-right", autoClose: 5000 });
            if (onRefresh) await onRefresh();
            onHide();
        } catch (error) {
            console.error("Erreur lors de l'annulation du transfert :", error);
            toast.error("Impossible d'annuler le transfert.", { position: "top-right", autoClose: 5000 });
        } finally {
            setIsCancelling(false);
            setIsConfirmationModalVisible(false);
        }
    };

    const handleCancelButtonPress = () => setIsConfirmationModalVisible(true);

    const accountDetails = getAccountDetails();

    // Vérifier si l'utilisateur est admin
    const isAdmin = currentUser?.roleNames?.includes('ADMIN');

    const renderDetailItem = (label, value, special = null) => (
        <div className="detail-item mb-3">
            <div className="text-muted small">{label}</div>
            <div
                className={`detail-value ${special === 'amount' ? 'fw-bold' : ''}`}
                style={{
                    color: special === 'status'  ? getStatusColor(transfer?.transactionStatus) :
                        special === 'method'  ? getMethodColor(value) : 'inherit',
                    fontSize: special === 'amount' ? '1.1rem' : 'inherit'
                }}
            >
                {value}
            </div>
        </div>
    );

    return (
        <>
            <style>{`
                .transfer-modal .modal-content { border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,.15); }
                .transfer-modal .modal-dialog  { max-width: 800px; width: 95%; }
                @media (min-width: 992px) { .transfer-modal .modal-dialog { max-width: 1000px; } }
                .transfer-modal-header  { background-color: #007bff; color: white; border-top-left-radius: 12px; border-top-right-radius: 12px; border-bottom: none; }
                .transfer-modal-title   { font-size: 1.25rem; font-weight: 600; }
                .transfer-modal-body    { padding: 1.5rem; }
                .info-card   { background-color: #f8f9fa; border-radius: 10px; padding: 1.25rem; margin-bottom: 1rem; border-left: 4px solid #007bff; }
                .reject-card { background-color: #f8f9fa; border-radius: 10px; padding: 1.25rem; margin-bottom: 1rem; border-left: 4px solid #FF3B30; }
                .card-title        { font-size: 1rem; font-weight: 600; color: #007bff; margin-left: .5rem; }
                .reject-card-title { font-size: 1rem; font-weight: 600; color: #FF3B30; margin-left: .5rem; }
                .detail-item  { margin-bottom: .75rem; }
                .detail-value { font-size: .95rem; }
                .status-badge { display: inline-block; padding: .25rem .75rem; border-radius: 1rem; font-size: .85rem; font-weight: 500; color: white; }
                .cancel-btn   { background-color: #FF3B30; border: none; padding: .5rem 1rem; font-weight: 500; }
                .cancel-btn:hover { background-color: #d63329; }
                .summary-container { text-align: center; margin-bottom: 1.5rem; }
                .amount-text { font-size: 1.5rem; font-weight: 700; margin-bottom: .5rem; }
                .card-header { display: flex; align-items: center; margin-bottom: 1rem; cursor: pointer; }
                .payment-directives-content ol, .payment-directives-content ul { padding-left: 1.2rem; }
                .payment-directives-content li { margin-bottom: .5rem; }
                .export-btn         { background-color: #007bff; border: none; padding: .5rem 1rem; font-weight: 500; }
                .export-btn:hover   { background-color: #0056b3; }
                .download-btn       { background-color: #34C759; border: none; padding: .5rem 1rem; font-weight: 500; }
                .download-btn:hover { background-color: #28a745; }
            `}</style>

            <Modal show={show} onHide={onHide} className="transfer-modal" centered>
                <Modal.Header closeButton className="transfer-modal-header">
                    <Modal.Title className="transfer-modal-title">
                        <i className="bi bi-arrow-left-right me-2"></i>
                        Détails du transfert
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="transfer-modal-body">
                    {transfer && (
                        <div className="transfer-modal-content">
                            {/* Résumé */}
                            <div className="summary-container">
                                <div className="amount-text">
                                    {formatMoney(transfer.transactionAmount, 'CAD')}
                                </div>
                                <span className="status-badge" style={{ backgroundColor: getStatusColor(transfer.transactionStatus) }}>
                                    {getStatusLabel(transfer.transactionStatus)}
                                </span>
                            </div>

                            {/* Infos transfert */}
                            <div className="info-card">
                                <div className="card-header">
                                    <i className="bi bi-info-circle-fill text-primary"></i>
                                    <span className="card-title">Informations de transfert</span>
                                </div>
                                {renderDetailItem("Banque émettrice", transfer.issuerBankName)}
                                {renderDetailItem("Pays", transfer.country)}
                                {renderDetailItem("Méthode de retrait", transfer.withdrawalMethod, 'method')}
                                {renderDetailItem("Nom de l'émetteur", transfer.issuerName)}
                                {renderDetailItem("Email de l'émetteur", transfer.issuerEmail)}
                                {renderDetailItem("Numéro de référence", transfer.referenceNumber)}
                                {renderDetailItem("Montant envoyé", formatMoney(transfer.transactionAmount, 'CAD'), 'amount')}
                                {transfer?.transactionStatus !== 'REJECTED' && transfer?.transactionReceiverAmount && (
                                    renderDetailItem(
                                        getReceiverAmountLabel(),
                                        formatMoney(transfer.transactionReceiverAmount, getCurrency(transfer.country)),
                                        'amount'
                                    )
                                )}
                            </div>

                            {/* Directives de paiement */}
                            {transfer.transactionStatus === 'PENDING' && (
                                <div className="info-card border-danger">
                                    <div className="card-header" onClick={togglePaymentDirectives}>
                                        <i className={`fas ${isPaymentDirectivesVisible ? 'fa-eye-slash' : 'fa-eye'} me-2`}></i>
                                        <span className="card-title">Directives de paiement</span>
                                    </div>
                                    {isPaymentDirectivesVisible && (
                                        <div className="payment-directives-content">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Chargement...</span>
                                                    </div>
                                                </div>
                                            ) : error ? (
                                                <div className="alert alert-danger text-center">{error}</div>
                                            ) : paymentSettings ? (
                                                <>
                                                    <p className="mb-2 mt-3">
                                                        <strong>Effectuez le virement Interac à l'adresse email suivante :</strong>
                                                    </p>
                                                    <p className="text-primary fs-5 fw-bold text-center mb-4">
                                                        {paymentSettings.emailInterac}
                                                    </p>
                                                    <p className="mb-3">
                                                        Notre adresse est configurée pour un dépôt automatique. Cependant, si une question et une réponse de sécurité vous sont demandées, veuillez entrer:
                                                    </p>
                                                    <div className="bg-light p-3 rounded mb-4">
                                                        <p className="mb-2"><strong>Nom :</strong> {paymentSettings.interacName}</p>
                                                        <p className="mb-2"><strong>Question :</strong> {paymentSettings.securityQuestion}</p>
                                                        <p className="mb-0"><strong>Réponse :</strong> {paymentSettings.securityAnswer}</p>
                                                    </div>
                                                    <p className="text-primary text-center mb-4">
                                                        Besoin d'aide? Appelez-nous! {ASSISTANCE_NUMBER}
                                                    </p>
                                                    <div className="alert alert-warning mb-0">
                                                        <p className="fw-bold mb-2">NB: Pour que la transaction soit exécutée, veuillez respecter les conditions suivantes :</p>
                                                        <ul className="mb-0">
                                                            <li>Le numéro de compte du destinataire doit être correct</li>
                                                            <li>L'email utilisé pour le virement doit correspondre à celui de votre inscription</li>
                                                            <li>Le nom du compte bancaire émetteur du virement Interac doit correspondre exactement au nom associé à votre compte sur notre application</li>
                                                        </ul>
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Destinataire */}
                            <div className="info-card border-warning">
                                <div className="card-header">
                                    <i className="bi bi-person-fill text-primary"></i>
                                    <span className="card-title">Destinataire</span>
                                </div>
                                {renderDetailItem("Prénom", transfer.recipientDetails.firstName)}
                                {renderDetailItem("Nom", transfer.recipientDetails.lastName)}
                                {renderDetailItem("Email", transfer.recipientDetails.email)}
                                {renderDetailItem("Ville", transfer.recipientDetails.city)}
                                {renderDetailItem("Pays", transfer.recipientDetails.country)}
                            </div>

                            {/* Compte */}
                            {accountDetails && (
                                <div className="info-card border-success">
                                    <div className="card-header">
                                        <i className={`bi ${accountDetails.type === 'BANK' ? 'bi-credit-card-fill' : 'bi-phone-fill'} text-primary`}></i>
                                        <span className="card-title">
                                            {accountDetails.type === 'BANK' ? 'Compte bancaire' : 'Portefeuille mobile'}
                                        </span>
                                    </div>
                                    {accountDetails.type === 'BANK' ? (
                                        <>
                                            {renderDetailItem("Nom du compte", accountDetails.details.accountName)}
                                            {renderDetailItem("Numéro de compte", accountDetails.details.accountNumber)}
                                            {renderDetailItem("Nom de la banque", accountDetails.details.branchName)}
                                            {renderDetailItem("Numéro d'agence", accountDetails.details.branchNumber)}
                                            {renderDetailItem("Ville", accountDetails.details.city)}
                                            {renderDetailItem("Adresse", accountDetails.details.address)}
                                        </>
                                    ) : (
                                        <>
                                            {renderDetailItem("Type de portefeuille", accountDetails.details.walletType)}
                                            {renderDetailItem("Numéro de téléphone", accountDetails.details.phoneNumber)}
                                            {renderDetailItem("Nom du portefeuille", accountDetails.details.accountName)}
                                            {renderDetailItem("Ville", accountDetails.details.city)}
                                            {renderDetailItem("Adresse", accountDetails.details.address)}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Motif de rejet */}
                            {transfer.rejectReason && (
                                <div className="reject-card">
                                    <div className="card-header">
                                        <i className="bi bi-exclamation-triangle-fill text-danger"></i>
                                        <span className="reject-card-title">Motif du rejet</span>
                                    </div>
                                    <div className="text-muted small">{transfer.rejectReason}</div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-top-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    {/* ── Bouton Annuler le transfert (gauche) ── */}
                    <div>
                        {transfer?.transactionStatus === 'PENDING' && currentUser?.username === transfer?.createdBy && (
                            isCancelling ? (
                                <div className="d-flex align-items-center text-danger">
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span>Annulation en cours...</span>
                                </div>
                            ) : (
                                <Button variant="danger" className="cancel-btn" onClick={handleCancelButtonPress}>
                                    <i className="fas fa-times me-2" />
                                    Annuler le transfert
                                </Button>
                            )
                        )}
                    </div>

                    {/* ── Bouton Export PDF (droite) - Visible uniquement pour l'admin ── */}
                    {isAdmin && (
                        <div>
                            {isExporting ? (
                                <Button variant="primary" disabled className="export-btn">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Génération du PDF...
                                </Button>
                            ) : downloadReady ? (
                                <Button variant="success" className="download-btn" onClick={handleDownload}>
                                    <i className="fas fa-cloud-download-alt me-2" />
                                    Télécharger — {fileName}
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    className="export-btn"
                                    onClick={() => handleExport(transfer?.id, transfer?.referenceNumber)}
                                    disabled={!transfer}
                                >
                                    <i className="fas fa-file-pdf me-2" />
                                    Exporter en PDF
                                </Button>
                            )}
                        </div>
                    )}
                </Modal.Footer>
            </Modal>

            <ConfirmationCancelTransferModal
                show={isConfirmationModalVisible}
                onHide={() => setIsConfirmationModalVisible(false)}
                onConfirm={handleCancelClick}
                message="Êtes-vous sûr de vouloir annuler ce transfert ? Cette action est irréversible."
            />
        </>
    );
};

export default TransferDetailModal;